import { useEffect } from 'react';

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useSelector } from 'react-redux';

import { selectAccountId } from '@/redux/session/selectors';
import { CallRecording, Channel } from '@/shared/types/analytics';
import { IntegrationType } from '@/shared/types/integration';

interface CallRecordingResponse {
  recording: CallRecording[];
}

interface RecordingProxyUrlParams {
  accountId: string;
  deskId: string;
  integrationId: string;
  recordingSid: string;
  channel: Channel;
  sessionId: string;
}

export const getRecordingProxyUrl = ({
  accountId,
  deskId,
  integrationId,
  recordingSid,
  channel,
  sessionId,
}: RecordingProxyUrlParams): string | null => {
  if (channel === IntegrationType.TWILIO_VOICE || channel === IntegrationType.TELNYX_VOICE) {
    return `/www/api/v1/desks/${encodeURIComponent(deskId)}/sessions/${encodeURIComponent(sessionId)}/recordings/${encodeURIComponent(recordingSid)}?integration_id=${encodeURIComponent(integrationId)}`;
  }
  // web-voice streams from R2 and needs session_id to compose the object prefix.
  if (channel === IntegrationType.WEB_VOICE) {
    return `/www/api/integrations/${channel}/accounts/${encodeURIComponent(accountId)}/desks/${encodeURIComponent(deskId)}/integrations/${encodeURIComponent(integrationId)}/sessions/${encodeURIComponent(sessionId)}/recordings/${encodeURIComponent(recordingSid)}`;
  }
  return null;
};

const GET_CALL_RECORDING = gql`
  query GetCallRecording($sessionId: String!, $accountId: uuid!) {
    recording: get_call_recording(args: { session_id: $sessionId, account_id: $accountId }) {
      account_id
      session_id
      recording_sid
      recording_status
      recording_url
      metadata
      created
      updated
    }
  }
`;

interface UseCallRecordingOptions {
  sessionId?: string;
  enabled?: boolean;
}

/**
 * Hook for fetching call recording data for a voice session.
 *
 * @param options.sessionId - The session ID to fetch recording for
 * @param options.enabled - Whether the query should be enabled (default: true when sessionId is provided)
 */
export const useCallRecording = ({ sessionId, enabled = true }: UseCallRecordingOptions) => {
  const accountId = useSelector(selectAccountId);

  const shouldSkip = !sessionId || !accountId || !enabled;

  const { data, loading, error, refetch, startPolling, stopPolling } =
    useQuery<CallRecordingResponse>(GET_CALL_RECORDING, {
      variables: { sessionId, accountId },
      skip: shouldSkip,
    });

  const recording = data?.recording?.[0] ?? null;

  // Poll every 5s while the recording is in-progress so the player auto-appears on completion
  useEffect(() => {
    if (recording?.recording_status === 'in-progress') {
      startPolling(5000);
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [recording?.recording_status, startPolling, stopPolling]);

  return {
    recording,
    isLoading: loading,
    isError: !!error,
    error,
    refetch,
    // Check for recording_sid since we use the voice provider API to fetch audio (not recording_url)
    hasRecording: !!recording?.recording_sid,
  };
};
